import { motion, useTime, useTransform } from 'framer-motion';

interface TerrainBackgroundProps {
  count?: number;
}

const LINES = [
  { y: '25%', width: '60%', x: '20%', opacity: 0.035, delay: 0 },
  { y: '45%', width: '80%', x: '5%', opacity: 0.025, delay: 0.4 },
  { y: '65%', width: '55%', x: '30%', opacity: 0.03, delay: 0.8 },
  { y: '80%', width: '70%', x: '10%', opacity: 0.02, delay: 1.2 },
];

const TerrainBackground = ({ count = 4 }: TerrainBackgroundProps) => {
  const time = useTime();
  // Slow drift upward — 1 full cycle every 20 seconds
  const y = useTransform(time, (t) => `${((t / 20000) % 1) * -100}%`);

  const lines = LINES.slice(0, Math.min(count, LINES.length));

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        {/* Repeat lines twice so the scroll loops seamlessly */}
        {[0, 1].map((rep) =>
          lines.map((line, i) => (
            <motion.div
              key={`${rep}-${i}`}
              className="absolute rounded-full"
              style={{
                top: `calc(${rep * 100}% + ${line.y})`,
                left: line.x,
                width: line.width,
                height: '1px',
                background: `hsl(var(--primary) / ${line.opacity})`,
              }}
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: line.delay + rep * 0.2, duration: 1.5, ease: 'easeOut' }}
            />
          ))
        )}
      </motion.div>

      {/* Subtle radial glow spots */}
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full"
        style={{
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.04) 0%, transparent 70%)',
          transform: 'translate(-50%, -50%)',
        }}
      />
      <div
        className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full"
        style={{
          background: 'radial-gradient(circle, hsl(var(--accent) / 0.04) 0%, transparent 70%)',
          transform: 'translate(50%, 50%)',
        }}
      />
    </div>
  );
};

export default TerrainBackground;
